import React, { useState } from 'react';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import SlideChart from './SlideChart';
import { resolveArcaAssetUrl } from '../utils/arcaApi';
import { HUD } from '../utils/slideThemes';



function SlideThumbnail({ slide, index, selected, dragging, dropTarget, onClick, onDragStart, onDragOver, onDrop, onDragEnd }) {

  const hasChart = (slide.graficos || []).length > 0;

  const hasImage = (slide.midias || []).length > 0;

  const firstChart = slide.graficos?.[0];

  const firstImage = slide.midias?.[0];
  const imageSrc = firstImage ? resolveArcaAssetUrl(firstImage.url || firstImage.src) : '';



  return (

    <Box

      draggable

      onDragStart={onDragStart}

      onDragOver={onDragOver}

      onDrop={onDrop}

      onDragEnd={onDragEnd}

      onClick={onClick}

      sx={{

        position: 'relative',

        cursor: dragging ? 'grabbing' : 'grab',

        borderRadius: 1,

        border: selected ? '2px solid #2563eb' : '1px solid #cbd5e1',

        outline: dropTarget ? '2px dashed #38bdf8' : 'none',

        outlineOffset: 2,

        bgcolor: '#0f172a',

        aspectRatio: '16 / 9',

        overflow: 'hidden',

        opacity: dragging ? 0.45 : 1,

        transition: 'border-color 0.15s, box-shadow 0.15s, opacity 0.15s',

        boxShadow: selected ? '0 0 0 1px rgba(37,99,235,0.35)' : 'none',

        '&:hover': { borderColor: selected ? '#2563eb' : '#94a3b8' },

      }}

    >

      <DragIndicatorIcon

        sx={{

          position: 'absolute',

          top: 2,

          right: 2,

          fontSize: 12,

          color: '#475569',

          pointerEvents: 'none',

        }}

      />

      <Typography

        variant="caption"

        sx={{

          position: 'absolute',

          top: 2,

          left: 4,

          color: '#64748b',

          fontSize: 9,

          fontWeight: 700,

          zIndex: 1,

        }}

      >

        {index + 1}

      </Typography>



      <Box sx={{ p: 0.75, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        <Typography

          noWrap

          sx={{

            color: '#f8fafc',

            fontSize: 8,

            fontWeight: 700,

            lineHeight: 1.2,

            mb: 0.25,

            pr: 1.5,

          }}

        >

          {slide.titulo || 'Sem título'}

        </Typography>



        {hasImage && imageSrc ? (

          <Box

            component="img"

            src={imageSrc}

            alt=""

            sx={{

              flex: 1,

              width: '100%',

              objectFit: 'cover',

              borderRadius: 0.5,

              pointerEvents: 'none',

            }}

          />

        ) : hasChart && firstChart ? (

          <Box sx={{ flex: 1, minHeight: 0, pointerEvents: 'none' }}>

            <SlideChart grafico={firstChart} compact />

          </Box>

        ) : (

          <Typography

            sx={{

              color: '#64748b',

              fontSize: 7,

              lineHeight: 1.3,

              overflow: 'hidden',

              display: '-webkit-box',

              WebkitLineClamp: 3,

              WebkitBoxOrient: 'vertical',

            }}

          >

            {(slide.bullets || []).filter(Boolean).join(' · ') || slide.subtitulo || 'Slide vazio'}

          </Typography>

        )}

      </Box>

    </Box>

  );

}



export default function SlideFilmstrip({

  slides,

  activeSlideId,

  onSelect,

  onAdd,

  onDuplicate,

  onRemove,

  onReorder,
  variant = 'light',
}) {
  const isHud = variant === 'hud';

  const [dragIndex, setDragIndex] = useState(null);

  const [overIndex, setOverIndex] = useState(null);



  const finishDrag = () => {

    setDragIndex(null);

    setOverIndex(null);

  };



  const handleDrop = (toIndex) => {

    if (dragIndex == null || dragIndex === toIndex) {

      finishDrag();

      return;

    }

    const next = [...slides];

    const [moved] = next.splice(dragIndex, 1);

    next.splice(toIndex, 0, moved);

    onReorder(next);

    finishDrag();

  };



  return (

    <Box

      sx={{

        width: { xs: '100%', md: '100%' },
        minWidth: { md: 148 },
        maxWidth: { md: 180 },

        flexShrink: 0,

        display: 'flex',

        flexDirection: { xs: 'row', md: 'column' },

        gap: 1,

        p: { xs: 0, md: 1 },

        borderRight: { md: isHud ? 'none' : '1px solid #e2e8f0' },
        borderBottom: { xs: '1px solid #e2e8f0', md: 'none' },
        bgcolor: isHud ? HUD.bgPanel : '#f1f5f9',

        maxHeight: { md: '100%' },

        overflowY: { md: 'auto' },

        overflowX: { xs: 'auto', md: 'hidden' },

      }}

    >

      <Tooltip title="Novo slide em branco">

        <IconButton

          size="small"

          onClick={onAdd}

          sx={{

            alignSelf: { md: 'center' },

            bgcolor: '#fff',

            border: '1px solid #cbd5e1',

            borderRadius: 1,

            width: { xs: 40, md: '100%' },

            height: 36,

            flexShrink: 0,

            '&:hover': { bgcolor: '#e2e8f0' },

          }}

        >

          <AddIcon fontSize="small" />

        </IconButton>

      </Tooltip>



      {slides.map((slide, index) => (

        <Box key={slide.id} sx={{ position: 'relative', flexShrink: 0, width: { xs: 128, md: '100%' } }}>

          <SlideThumbnail

            slide={slide}

            index={index}

            selected={slide.id === activeSlideId}

            dragging={dragIndex === index}

            dropTarget={overIndex === index && dragIndex !== index}

            onClick={() => onSelect(slide.id)}

            onDragStart={(e) => {

              e.dataTransfer.effectAllowed = 'move';

              e.dataTransfer.setData('text/plain', String(index));

              setDragIndex(index);

            }}

            onDragOver={(e) => {

              e.preventDefault();

              e.dataTransfer.dropEffect = 'move';

              if (dragIndex !== index) setOverIndex(index);

            }}

            onDrop={(e) => {

              e.preventDefault();

              e.stopPropagation();

              handleDrop(index);

            }}

            onDragEnd={finishDrag}

          />

          {slide.id === activeSlideId && slides.length > 0 && (

            <Box

              className="arca-export-hide"

              sx={{

                display: 'flex',

                justifyContent: 'center',

                gap: 0.25,

                mt: 0.5,

              }}

            >

              <Tooltip title="Duplicar slide">

                <IconButton size="small" onClick={() => onDuplicate(slide.id)} sx={{ p: 0.25 }}>

                  <ContentCopyIcon sx={{ fontSize: 14 }} />

                </IconButton>

              </Tooltip>

              {slides.length > 1 && (

                <Tooltip title="Excluir slide">

                  <IconButton

                    size="small"

                    onClick={() => onRemove(slide.id)}

                    sx={{ p: 0.25, color: '#dc2626' }}

                  >

                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />

                  </IconButton>

                </Tooltip>

              )}

            </Box>

          )}

        </Box>

      ))}

    </Box>

  );

}

